import React, { useEffect, useState, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import { Refresh as RefreshIcon, UploadFile as UploadFileIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import { createJob, listJobs, getSubjects } from "./test-upload.service";

const STATUS_CONFIG = {
  UPLOADED: { label: "Uploaded", color: "#8492a6" },
  EXTRACTING: { label: "Extracting…", color: "#8b5cf6" },
  CLASSIFYING: { label: "Classifying…", color: "#8b5cf6" },
  EXTRACTION_FAILED: { label: "Extraction failed", color: "#dc3545" },
  READY_FOR_REVIEW: { label: "Ready for review", color: "#3366FF" },
  IN_REVIEW: { label: "In review", color: "#3366FF" },
  APPROVED: { label: "Approved", color: "#22c55e" },
  PUBLISHING: { label: "Publishing…", color: "#8b5cf6" },
  PUBLISHED: { label: "Published", color: "#16a34a" },
  PUBLISH_FAILED: { label: "Publish failed", color: "#dc3545" },
  REJECTED: { label: "Rejected", color: "#8492a6" },
  CANCELLED: { label: "Cancelled", color: "#8492a6" },
};

const IN_PROGRESS = ["UPLOADED", "EXTRACTING", "CLASSIFYING", "PUBLISHING"];

export default function TestUploadList() {
  const history = useHistory();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [file, setFile] = useState(null);
  const [jobName, setJobName] = useState("");
  const [defaultSubjectId, setDefaultSubjectId] = useState("");
  const [testType, setTestType] = useState("");
  // Per-upload AI classification toggles — default on; faculty unticks any level they want
  // to set manually at review (or to skip entirely to save OpenAI cost + time).
  const [classifySubject, setClassifySubject] = useState(true);
  const [classifyTopic, setClassifyTopic] = useState(true);
  const [classifySubtopic, setClassifySubtopic] = useState(true);
  const [uploading, setUploading] = useState(false);
  const pollRef = useRef(null);

  const loadJobs = useCallback(async () => {
    try {
      const res = await listJobs();
      setJobs(res?.result || []);
    } catch (e) {
      toast.error(typeof e === "string" ? e : "Failed to load jobs");
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    loadJobs().finally(() => setLoading(false));
    getSubjects()
      .then((res) => setSubjects(res?.result || []))
      .catch(() => setSubjects([]));
  }, [loadJobs]);

  // Poll while any job is in progress
  useEffect(() => {
    const anyInProgress = jobs.some((j) => IN_PROGRESS.includes(j.status));
    if (anyInProgress && !pollRef.current) {
      pollRef.current = setInterval(loadJobs, 5000);
    } else if (!anyInProgress && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [jobs, loadJobs]);

  const handleUpload = async () => {
    if (!file) {
      toast.warn("Please choose a .docx, .pptx, or .html file");
      return;
    }
    if (!jobName.trim()) {
      toast.warn("Please enter a name for this upload");
      return;
    }
    setUploading(true);
    try {
      await createJob(file, {
        jobName: jobName.trim(),
        defaultSubjectId: defaultSubjectId || undefined,
        testType: testType || undefined,
        classifySubject,
        classifyTopic,
        classifySubtopic,
      });
      toast.success(
        classifySubject || classifyTopic || classifySubtopic
          ? "Upload accepted. Extraction has started."
          : "Upload accepted. Extraction started — AI classification skipped per your selection."
      );
      setFile(null);
      setJobName("");
      setDefaultSubjectId("");
      setTestType("");
      setClassifySubject(true);
      setClassifyTopic(true);
      setClassifySubtopic(true);
      loadJobs();
    } catch (e) {
      toast.error(typeof e === "string" ? e : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const renderProgress = (job) => {
    const total = job.totalQuestions || 0;
    if (total === 0) {
      return IN_PROGRESS.includes(job.status) ? <LinearProgress sx={{ width: 120 }} /> : "—";
    }
    const pct = Math.round(((job.classifiedCount || job.extractedCount || 0) / total) * 100);
    return `${job.extractedCount || 0}/${total} extracted` + (job.classifiedCount ? `, ${pct}% classified` : "");
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Test Upload (AI-assisted)
      </Typography>

      {/* Upload form */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Upload a question document
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
          <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
            {file ? file.name : "Choose .docx / .pptx / .html"}
            <input
              hidden
              type="file"
              accept=".docx,.pptx,.html"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </Button>
          <TextField
            label="Upload name"
            size="small"
            value={jobName}
            onChange={(e) => setJobName(e.target.value)}
            sx={{ minWidth: 220 }}
            placeholder="e.g. FMGE Grand Test 12 - Anatomy"
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Default subject (optional)</InputLabel>
            <Select
              label="Default subject (optional)"
              value={defaultSubjectId}
              onChange={(e) => setDefaultSubjectId(e.target.value)}
            >
              <MenuItem value="">
                <em>Let AI detect</em>
              </MenuItem>
              {subjects.map((s) => (
                <MenuItem key={s.subjectId} value={s.subjectId}>
                  {s.subject}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Test type</InputLabel>
            <Select label="Test type" value={testType} onChange={(e) => setTestType(e.target.value)}>
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="FMGE">FMGE</MenuItem>
              <MenuItem value="NEET">NEET</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" disabled={uploading} onClick={handleUpload}>
            {uploading ? <CircularProgress size={20} color="inherit" /> : "Upload & Extract"}
          </Button>
        </Box>
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            AI classification (untick a level to set it manually at review time):
          </Typography>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={classifySubject}
                  onChange={(e) => setClassifySubject(e.target.checked)}
                />
              }
              label="Subject"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={classifyTopic}
                  onChange={(e) => setClassifyTopic(e.target.checked)}
                />
              }
              label="Topic"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={classifySubtopic}
                  onChange={(e) => setClassifySubtopic(e.target.checked)}
                />
              }
              label="Sub-topic"
            />
          </FormGroup>
          {!classifySubject && !classifyTopic && !classifySubtopic && (
            <Typography variant="caption" color="warning.main" sx={{ display: "block" }}>
              AI classification is fully off — questions will go straight to review with no
              suggested subject / topic / sub-topic.
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Phase 1 supports the table-format document. Extraction + AI classification run in the
          background; large files (1000–2000 questions) can take several minutes.
        </Typography>
      </Paper>

      {/* Jobs table */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Uploads
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={loadJobs}>
          Refresh
        </Button>
      </Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Progress</strong></TableCell>
              <TableCell><strong>Uploaded by</strong></TableCell>
              <TableCell><strong>Created</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : jobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No uploads yet.
                </TableCell>
              </TableRow>
            ) : (
              jobs.map((job) => {
                const cfg = STATUS_CONFIG[job.status] || { label: job.status, color: "#666" };
                const canReview = ["READY_FOR_REVIEW", "IN_REVIEW", "APPROVED"].includes(job.status);
                return (
                  <TableRow key={job.jobId} hover>
                    <TableCell>{job.jobName}</TableCell>
                    <TableCell>
                      <Chip
                        label={cfg.label}
                        size="small"
                        sx={{ backgroundColor: cfg.color, color: "#fff" }}
                      />
                      {job.status === "EXTRACTION_FAILED" && job.errorSummary && (
                        <Typography variant="caption" color="error" display="block">
                          {job.errorSummary}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{renderProgress(job)}</TableCell>
                    <TableCell>{job.uploadedBy}</TableCell>
                    <TableCell>{job.createdDate}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!canReview}
                        onClick={() => history.push(`/app/test-upload/review/${job.jobId}`)}
                      >
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
